import { useCanvasById } from "@/api/canvas";
import { useCheckBulkPermissions } from "@/api/relations";
import type { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext } from "react";

import { useCanvasReplay } from "../context/CanvasReplayContext";
import { CanvasRouteContext } from "../context/CanvasRouteContext";

/**
 * Bulk permission checks for the current canvas (`can_edit`, `can_manage_assignments`, etc.).
 *
 * By default reads `canvasId` from `CanvasRouteProvider`. Pass `canvasId` when the hook runs
 * outside that provider (e.g. `CanvasExplorerPane` in the app layout shell).
 *
 * `canComment` is the single choke point for canvas comments: every surface —
 * pins, thread windows, the rail section, the chrome verb, the entry points —
 * hangs off it, so the `canvas-comments` flag is folded in here rather than at
 * each mount. Fails closed.
 *
 * A trashed canvas is read-only, annotations included: both verbs fall to
 * false while `archived_at` is set, so every surface follows at once and
 * restore brings them back together. Replay does the same while it runs.
 */
export function useCanvasPermissions(canvasIdOverride?: string) {
  const routeCtx = useContext(CanvasRouteContext);
  const canvasId = canvasIdOverride ?? routeCtx?.canvasId;
  const flags = useFlags<Flags>();
  const { data: permissions } = useCheckBulkPermissions({
    checks: canvasId
      ? [
          {
            resource_object_type: "canvas",
            resource_object_id: canvasId,
            permission: "can_view",
          },
          {
            resource_object_type: "canvas",
            resource_object_id: canvasId,
            permission: "can_edit",
          },
          {
            resource_object_type: "canvas",
            resource_object_id: canvasId,
            permission: "can_comment",
          },
          {
            resource_object_type: "canvas",
            resource_object_id: canvasId,
            permission: "can_delete",
          },
          {
            resource_object_type: "canvas",
            resource_object_id: canvasId,
            permission: "can_manage_assignments",
          },
        ]
      : [],
  });

  const { data: canvas } = useCanvasById(canvasId);
  const replaying = useCanvasReplay()?.replaying ?? false;
  const readOnly = !!canvas?.archived_at || replaying;

  const canEdit =
    canvasId && !readOnly
      ? (permissions?.canvas?.[canvasId]?.can_edit ?? false)
      : false;
  const canManageAssignments = canvasId
    ? (permissions?.canvas?.[canvasId]?.can_manage_assignments ?? false)
    : false;
  const canComment =
    canvasId && !readOnly
      ? (permissions?.canvas?.[canvasId]?.can_comment ?? false) &&
        !!flags.canvasComments
      : false;

  return { canEdit, canManageAssignments, canComment };
}
